'use client';

import { REVIEW_SOURCE_ICON_PATHS } from '@/utils/reviewSourceAlt';
import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactPlayer from 'react-player';
import {
    getFacebookEmbedUrl,
    isFacebookVideoUrl,
    isVideoUrl,
} from '@/utils/isVideoUrl';
import {
    CodeBracketIcon,
    PlayIcon,
    PhotoIcon,
} from '@heroicons/react/24/outline';
import { useEditor } from './context/EditorContext/EditorContext';

const STOCK_IMAGES = [
    REVIEW_SOURCE_ICON_PATHS.google,
    REVIEW_SOURCE_ICON_PATHS.nicejob,
    REVIEW_SOURCE_ICON_PATHS.facebook,
];

/* ----------------------------- URL helpers ----------------------------- */

const isImageUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const u = url.trim();
    return (
        /^data:image\//i.test(u) ||
        /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(u)
    );
};

const isSvgMarkup = (val) =>
    typeof val === 'string' && /<\s*svg[\s>]/i.test(val);

const svgMarkupToDataUrl = (markup) => {
    const value = String(markup || '').trim();
    if (!value) return '';
    try {
        const utf8 =
            typeof window !== 'undefined' && typeof window.btoa === 'function'
                ? window.btoa(unescape(encodeURIComponent(value)))
                : btoa(unescape(encodeURIComponent(value)));
        return `data:image/svg+xml;base64,${utf8}`;
    } catch {
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(value)}`;
    }
};

/* Raw embed (iframe/script/video/div/etc.) detector */
const isEmbedCode = (val) =>
    typeof val === 'string' &&
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i.test(
        val
    );

const getMediaType = (value) => {
    const val = String(value || '').trim();
    if (!val) return null;
    if (isSvgMarkup(val)) return 'image';
    if (isEmbedCode(val)) return 'embed';
    if (isVideoUrl(val)) return 'video';
    if (isImageUrl(val)) return 'image';
    return 'unknown';
};

const getImagePreviewSrc = (value) => {
    const val = String(value || '').trim();
    if (!val) return '';
    if (isSvgMarkup(val)) {
        return svgMarkupToDataUrl(val);
    }
    return val;
};

function SvgMarkupPreview({ markup, className = '' }) {
    return (
        <div
            className={className}
            dangerouslySetInnerHTML={{ __html: String(markup || '').trim() }}
        />
    );
}

const getMediaDisplayName = (value, type) => {
    const val = String(value || '').trim();
    if (!val) return 'Selected media';
    if (isSvgMarkup(val)) return 'SVG image';
    if (type === 'embed') return 'Custom embed';
    if (type === 'video') return 'External video';
    try {
        const normalized = val.startsWith('http') ? new URL(val).pathname : val;
        const fileName = normalized.split('/').pop() || '';
        return decodeURIComponent(fileName || val);
    } catch {
        return val;
    }
};

/* -------------------------- Gradient Field UI -------------------------- */
function GradientField({ children, className = '' }) {
    return (
        <div
            className={[
                'rounded-xl bg-neutral-300 p-[2px]',
                'focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600',
                className,
            ].join(' ')}
        >
            {children}
        </div>
    );
}

function ImageThumbnail({
    value,
    alt,
    className = '',
    svgClassName = '',
    draggable = false,
}) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return null;

    if (isSvgMarkup(normalizedValue)) {
        return (
            <SvgMarkupPreview
                markup={normalizedValue}
                className={svgClassName || className}
            />
        );
    }

    return (
        <img
            src={getImagePreviewSrc(normalizedValue)}
            alt={alt}
            className={className}
            draggable={draggable}
        />
    );
}

/* ---------------------- Unified Media Panel ---------------------- */
export function MediaPanel({
    onSelectImage,
    onSelectVideo,
    onSelectCustom,
    onPreviewSelect,
    enableVideo = true,
    currentValue,
    showBackgroundVideoWarning = false,
    inline = false,
    hidePreview = false,
    hideExternalInput = false,
}) {
    const editor = useEditor();
    const [inputValue, setInputValue] = useState('');
    const [previewValue, setPreviewValue] = useState('');
    const [isExternalFocused, setIsExternalFocused] = useState(false);

    /* ---------- Prefill when opening based on currentValue ---------- */
    useEffect(() => {
        const initial = currentValue ? String(currentValue).trim() : '';
        setInputValue(initial);
        setPreviewValue(initial);
    }, [currentValue]);

    /* ---------- Live preview for typed links ---------- */
    useEffect(() => {
        const next = inputValue.trim();
        setPreviewValue(next);
    }, [inputValue]);

    /* ---------- Auto-Detect Type ---------- */
    const detectedType = useMemo(() => getMediaType(inputValue), [inputValue]);

    const previewType = useMemo(
        () => getMediaType(previewValue),
        [previewValue]
    );

    /* ---------- Actions ---------- */
    const applyMediaValue = useCallback(
        (value) => {
            const val = String(value || '').trim();
            const type = getMediaType(val);
            if (!val) {
                if (inline) {
                    onSelectImage?.('');
                    return;
                }
                onPreviewSelect?.('');
                return;
            }
            if (inline) {
                if (type === 'embed') {
                    onSelectCustom?.(val);
                    return;
                }
                if (type === 'video') {
                    if (enableVideo) onSelectVideo?.(val);
                    return;
                }
                onSelectImage?.(val);
                return;
            }
            onPreviewSelect?.(val);
        },
        [
            enableVideo,
            inline,
            onPreviewSelect,
            onSelectCustom,
            onSelectImage,
            onSelectVideo,
        ]
    );

    const handleApply = useCallback(() => {
        const val = inputValue.trim();
        if (!val) return;

        setPreviewValue(val);
        const type = getMediaType(val);

        if (inline) {
            applyMediaValue(val);
            return;
        }

        if (type === 'embed') {
            onSelectCustom?.(val);
            return;
        }

        if (type === 'video') {
            if (enableVideo) onSelectVideo?.(val);
            return;
        }

        onSelectImage?.(val);
    }, [
        applyMediaValue,
        enableVideo,
        inline,
        inputValue,
        onSelectCustom,
        onSelectImage,
        onSelectVideo,
    ]);

    const handleInputChange = useCallback(
        (nextRawValue) => {
            setInputValue(nextRawValue);

            const nextValue = String(nextRawValue || '').trim();
            setPreviewValue(nextValue);

            if (!inline) return;

            const nextType = getMediaType(nextValue);

            if (!nextValue) {
                applyMediaValue('');
                return;
            }

            if (nextType === 'unknown') return;
            if (nextType === 'video' && !enableVideo) return;

            applyMediaValue(nextValue);
        },
        [applyMediaValue, enableVideo, inline]
    );

    const isValid = detectedType && detectedType !== 'unknown';
    const isVideoAndDisabled = detectedType === 'video' && !enableVideo;
    const selectedAssetValue = String(
        inline ? currentValue ?? '' : previewValue ?? ''
    ).trim();
    const selectedAssetType = getMediaType(selectedAssetValue);

    const images = editor.site?.data?.images;
    const isImagesLoading = images === undefined;
    const hasSelectedLibraryImage = Boolean(
        selectedAssetValue &&
            (images?.some((image) => image.url === selectedAssetValue) ||
                STOCK_IMAGES.includes(selectedAssetValue))
    );
    const selectedExternalAssetValue =
        selectedAssetValue && !hasSelectedLibraryImage
            ? selectedAssetValue
            : '';
    const selectedExternalAssetName = getMediaDisplayName(
        selectedExternalAssetValue,
        selectedAssetType
    );

    const handleImageClick = (url) => {
        const normalizedUrl = String(url || '').trim();
        const isAlreadySelected =
            normalizedUrl.length > 0 && selectedAssetValue === normalizedUrl;
        if (isAlreadySelected) {
            setInputValue('');
            setPreviewValue('');
            applyMediaValue('');
            return;
        }

        setInputValue(url);
        setPreviewValue(url);
        applyMediaValue(url);
    };

    const renderPreview = () => {
        const baseScrollClasses =
            'inline-flex self-start border border-neutral-200 rounded-[6px] overflow-hidden shadow';
        const scrollStyle = {
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'auto',
        };

        if (!previewValue) {
            return (
                <div
                    className={`inline-flex h-60 max-w-3xl items-center justify-center ${baseScrollClasses} border-dashed border-neutral-300 bg-white px-4 text-xs text-neutral-400`}
                    style={scrollStyle}
                >
                    Select an image or paste a link below to preview it here.
                </div>
            );
        }

        if (previewType === 'video') {
            return (
                <div
                    className={`relative w-full max-w-3xl ${baseScrollClasses} bg-black/70`}
                    style={{ ...scrollStyle, aspectRatio: '16 / 9' }}
                >
                    {isFacebookVideoUrl(previewValue) ? (
                        <iframe
                            title="Facebook video preview"
                            src={getFacebookEmbedUrl(previewValue)}
                            className="absolute left-0 top-0 h-full w-full"
                            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                            allowFullScreen
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                        />
                    ) : (
                        <ReactPlayer
                            url={previewValue}
                            controls
                            width="100%"
                            height="100%"
                        />
                    )}
                </div>
            );
        }

        if (previewType === 'image') {
            return (
                <div
                    className={`inline-flex max-w-3xl items-center justify-start ${baseScrollClasses} bg-white`}
                >
                    <ImageThumbnail
                        value={previewValue}
                        alt="Selected preview"
                        className="block h-auto max-h-60 w-full max-w-full select-none object-contain"
                        svgClassName="block h-auto max-h-60 w-full max-w-full select-none [&>svg]:h-auto [&>svg]:max-h-60 [&>svg]:w-full [&>svg]:max-w-full"
                    />
                </div>
            );
        }

        if (previewType === 'embed') {
            return (
                <div
                    className={`inline-flex min-h-[5rem] w-full items-stretch justify-start ${baseScrollClasses} overflow-auto bg-white`}
                    style={{ ...scrollStyle, maxHeight: '18rem' }}
                >
                    <div
                        className="min-h-full min-w-full text-[11px] text-neutral-600"
                        dangerouslySetInnerHTML={{ __html: previewValue }}
                    />
                </div>
            );
        }

        return (
            <div
                className={`inline-flex h-24 max-w-3xl items-center justify-center ${baseScrollClasses} border-neutral-200 bg-white text-xs text-neutral-500`}
                style={scrollStyle}
            >
                Preview not available for this link.
            </div>
        );
    };

    /* ----------------------------- Render ----------------------------- */
    return (
        <div
            className={
                inline
                    ? 'flex flex-col gap-2.5'
                    : 'absolute inset-0 flex h-full flex-col'
            }
        >
            {/* TOP SECTION: Preview + Gallery (shared scroll when space is tight) */}
            <div className={inline ? '' : 'flex-1 overflow-hidden pt-4'}>
                <div
                    className={
                        inline
                            ? 'flex flex-col gap-2.5'
                            : 'flex h-full min-h-0 flex-col gap-4 overflow-y-auto'
                    }
                >
                    {!hidePreview ? (
                        <div className="shrink-0">
                            <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs font-semibold text-neutral-700">
                                    Preview
                                </span>
                            </div>
                            {renderPreview()}
                        </div>
                    ) : null}

                    <div
                        className={`min-h-[12rem] rounded-xl p-[2px] transition-colors ${
                            inline
                                ? 'w-full max-w-[19rem] self-start'
                                : 'flex-1'
                        } ${
                            !isExternalFocused && previewValue
                                ? 'bg-gradient-to-br from-primary-600 to-secondary-600'
                                : 'bg-neutral-200'
                        }`}
                    >
                        <div
                            className={`min-h-[10rem] overflow-y-auto rounded-[10px] bg-white p-3 ${
                                inline ? 'max-h-[20rem]' : 'h-full'
                            }`}
                        >
                            <div className="flex flex-wrap items-start gap-4">
                                {selectedExternalAssetValue ? (
                                    <div className="flex w-20 flex-col items-center gap-1">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                handleImageClick(
                                                    selectedExternalAssetValue
                                                )
                                            }
                                            className="group flex w-20 flex-col items-center gap-1 text-left"
                                        >
                                            <div className="relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-neutral-200/50 shadow-sm ring-2 ring-primary-400 transition-all duration-150">
                                                {selectedAssetType ===
                                                'image' ? (
                                                    <ImageThumbnail
                                                        value={
                                                            selectedExternalAssetValue
                                                        }
                                                        alt={
                                                            selectedExternalAssetName
                                                        }
                                                        className="block h-full w-full object-contain"
                                                        svgClassName="flex h-full w-full items-center justify-center overflow-hidden [&>svg]:h-full [&>svg]:max-h-full [&>svg]:w-full [&>svg]:max-w-full"
                                                    />
                                                ) : selectedAssetType ===
                                                  'video' ? (
                                                    <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-900 text-white">
                                                        <PlayIcon className="mb-1 h-6 w-6" />
                                                        <span className="text-[9px] font-semibold uppercase tracking-[0.12em]">
                                                            Video
                                                        </span>
                                                    </div>
                                                ) : selectedAssetType ===
                                                  'embed' ? (
                                                    <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-900 text-white">
                                                        <CodeBracketIcon className="mb-1 h-6 w-6" />
                                                        <span className="text-[9px] font-semibold uppercase tracking-[0.12em]">
                                                            Embed
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div className="flex h-full w-full flex-col items-center justify-center bg-neutral-100 text-neutral-500">
                                                        <PhotoIcon className="mb-1 h-6 w-6" />
                                                        <span className="text-[9px] font-semibold uppercase tracking-[0.12em]">
                                                            External
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            <div
                                                className="max-w-[5rem] truncate text-center text-[9px] leading-4 text-primary-700"
                                                title={
                                                    selectedExternalAssetName
                                                }
                                            >
                                                {selectedExternalAssetName}
                                            </div>
                                        </button>
                                    </div>
                                ) : null}
                                {isImagesLoading && (
                                    <div className="w-full py-6 text-center text-xs text-neutral-400">
                                        <div className="mx-auto mb-2 h-5 w-5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
                                        Loading images...
                                    </div>
                                )}
                                {images?.map((image) => {
                                    const displayName =
                                        image?.filename ||
                                        image?.url
                                            ?.split('/')
                                            .pop()
                                            ?.split('?')[0] ||
                                        'Untitled';
                                    const isSelected =
                                        selectedAssetValue === image.url;
                                    return (
                                        <div
                                            key={image.image_id}
                                            className="flex w-20 flex-col items-center gap-1"
                                        >
                                            <div
                                                className={`group relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded bg-neutral-200/50 shadow-sm transition-all duration-150 ${
                                                    isSelected
                                                        ? 'ring-2 ring-primary-400'
                                                        : 'hover:ring-2 hover:ring-primary-300'
                                                }`}
                                                style={{ aspectRatio: '4 / 3' }}
                                            >
                                                <button
                                                    type="button"
                                                    className="pointer-events-auto absolute right-1 top-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-neutral-900/80 text-white opacity-0 shadow transition-opacity duration-150 hover:bg-neutral-900 group-hover:opacity-100"
                                                    title="Delete"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        editor.site.deleteImage?.(
                                                            {
                                                                image_id:
                                                                    image.image_id,
                                                            }
                                                        );
                                                    }}
                                                >
                                                    <i className="fas fa-xmark text-[8px] leading-none" />
                                                </button>
                                                <div className="absolute inset-1">
                                                    <img
                                                        src={image.url}
                                                        alt={displayName}
                                                        className="block h-full w-full cursor-pointer object-contain"
                                                        draggable={false}
                                                        title="Select"
                                                        onClick={() =>
                                                            handleImageClick(
                                                                image.url
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                            <div
                                                className="max-w-[5rem] truncate text-center text-[9px] leading-4 text-neutral-600"
                                                title={displayName}
                                            >
                                                {displayName}
                                            </div>
                                        </div>
                                    );
                                })}
                                {STOCK_IMAGES.map((url) => {
                                    const isSelected =
                                        selectedAssetValue === url;
                                    return (
                                        <div
                                            key={url}
                                            className={`relative inline-flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded border bg-white shadow-sm transition ${
                                                isSelected
                                                    ? 'border-primary-400 ring-2 ring-primary-300'
                                                    : 'border-neutral-200 hover:border-primary-400'
                                            }`}
                                            style={{ aspectRatio: '4 / 3' }}
                                            onClick={() =>
                                                handleImageClick(url)
                                            }
                                        >
                                            <img
                                                src={url}
                                                alt="Stock asset"
                                                className="block h-full w-full cursor-pointer object-contain"
                                                draggable={false}
                                            />
                                        </div>
                                    );
                                })}
                                {(!editor.site?.data?.images ||
                                    editor.site.data.images.length === 0) && (
                                    <div className="w-full py-8 text-center text-xs text-neutral-500">
                                        <div className="mb-3">
                                            No images uploaded yet.
                                        </div>
                                        <div className="flex flex-wrap justify-center gap-3">
                                            {STOCK_IMAGES.map((url) => (
                                                <button
                                                    key={url}
                                                    type="button"
                                                    onClick={() =>
                                                        handleImageClick(url)
                                                    }
                                                    className="h-16 w-20 overflow-hidden rounded border border-neutral-200 bg-white shadow-sm transition hover:border-primary-400"
                                                >
                                                    <img
                                                        src={url}
                                                        alt="Stock asset"
                                                        className="h-full w-full object-contain"
                                                        draggable={false}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* BOTTOM SECTION: Unified Input */}
            {!hideExternalInput ? (
                <div
                    className={inline ? 'shrink-0 pt-2' : 'shrink-0 pb-6 pt-4'}
                >
                    <div
                        className={
                            inline
                                ? 'flex flex-col gap-3'
                                : 'flex flex-col gap-3 rounded-xl bg-white p-3 shadow-sm'
                        }
                    >
                        <div className="flex items-center justify-between">
                            <label className="text-[11px] font-semibold text-primary-700">
                                Add External Media
                            </label>
                            {inputValue && (
                                <span
                                    className={`text-[9px] font-bold uppercase tracking-[0.14em] ${
                                        isVideoAndDisabled
                                            ? 'text-red-500'
                                            : 'text-primary-600'
                                    }`}
                                >
                                    {isVideoAndDisabled
                                        ? 'Video Disabled'
                                        : detectedType === 'unknown'
                                        ? 'Unknown Format'
                                        : detectedType}
                                </span>
                            )}
                        </div>

                        <div className="flex items-start gap-2">
                            <GradientField className="flex-1">
                                <textarea
                                    rows={3}
                                    value={inputValue}
                                    onChange={(e) =>
                                        handleInputChange(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' &&
                                            !e.shiftKey &&
                                            isValid &&
                                            !isVideoAndDisabled
                                        ) {
                                            e.preventDefault();
                                            handleApply();
                                        }
                                    }}
                                    onFocus={() => setIsExternalFocused(true)}
                                    onBlur={() => setIsExternalFocused(false)}
                                    className="block w-full resize-none rounded-[10px] border-0 bg-white px-3 py-2 text-[11px] leading-4 text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:ring-0"
                                    placeholder="Paste an Image URL, Video URL (YouTube/Vimeo/MP4), or HTML Embed Code here..."
                                />
                            </GradientField>
                        </div>
                        {showBackgroundVideoWarning &&
                            isFacebookVideoUrl(inputValue) && (
                                <p className="mt-2 text-[10px] text-amber-700">
                                    Facebook videos and reels cannot autoplay as
                                    background videos. Please use another
                                    provider.
                                </p>
                            )}
                    </div>
                </div>
            ) : null}
            {!inline ? (
                <div className="mx-[-1rem] shrink-0 bg-gradient-to-r from-primary-700 to-secondary-700 px-6 py-3">
                    <div className="flex justify-end">
                        <button
                            type="button"
                            className="inline-flex min-w-[6rem] items-center justify-center rounded-4xl border border-white bg-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-60"
                            onClick={handleApply}
                            disabled={!isValid || isVideoAndDisabled}
                        >
                            Apply
                        </button>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
